import React, { useRef, useState, useEffect } from "react";
import "./notificationStyle.css";
import Select from "react-select";
import _ from "lodash";
import Tooltip from "../../components/Tooltip";
import DeleteRecipientOption from "../DeleteRecipientOption";
import useGetOrderStatuses from "../../hooks/Order/useGetOrderStatuses";
import useUpdateRecipient from "../../hooks/Order/useUpdateRecipient";
import useCreateRecipient from "../../hooks/Order/useCreateRecipient";
import Loader from "react-loader-spinner";

export default function CreateRecipientPopup({
  visible,
  onClose,
  onSave,
  notification,
  error,
  loading,
}) {
  const modal = useRef(null);
  const { createRecipient } = useCreateRecipient();
  const { updateRecipient } = useUpdateRecipient();

  // const [communicationType, setCommunicationType] = useState(null);
  // const [frequency, setFrequency] = useState(null);

  //Recipient input
  const [firstName, setFirstName] = useState(notification?.firstName || "");
  const [lastName, setLastName] = useState(notification?.lastName || "");
  const [email, setEmail] = useState(notification?.email || "");
  const [phone, setPhone] = useState(notification?.phoneNumber || "");
  const [orderStatus, setOrderStatus] = useState("");
  const [communicationType, setCommunicationType] = useState("");
  const [frequency, setFrequency] = useState("");
  const [entries, setEntries] = useState(notification?.subscriptions || []);

  //Error message
  const [firstNameErrorMessage, setFirstNameErrorMessage] = useState("");
  const [lastNameErrorMessage, setLastNameErrorMessage] = useState("");
  const [emailErrorMessage, setEmailErrorMessage] = useState("");
  const [phoneErrorMessage, setPhoneErrorMessage] = useState("");
  const [statusErrorMessage, setStatusErrorMessage] = useState("");
  const [communicationTypeErrorMessage, setCommunicationTypeErrorMessage] =
    useState("");
  const [frequencyErrorMessage, setFrequencyErrorMessage] = useState("");
  const [duplicateStatusErrorMessage, setDuplicateStatusErrorMessage] =
    useState("");

  const [onSubmiting, setOnSubmiting] = useState(false);
  const [smsConsent, setSmsConsent] = useState(false);

  useEffect(() => {
    const smsEntriesCount = entries.filter(
      (entry) => entry.communicationType === "SMS",
    ).length;

    if (notification) {
      const smsCount =
        notification.subscriptions?.filter(
          (sub) => sub.communicationType === "SMS",
        ).length || 0;
      if (smsCount > 0) {
        setSmsConsent(true);
      } else {
        setSmsConsent(smsEntriesCount > 1);
      }
    } else {
      setSmsConsent(smsEntriesCount > 1);
    }
  }, [entries, notification]);

  const { data: orderStatuses } = useGetOrderStatuses();

  const communicationTypeOptions = [
    {
      label: "EMAIL",
      value: "EMAIL",
    },
    {
      label: "SMS",
      value: "SMS",
    },
  ];

  const frequencyOptions = [
    {
      label: "DAILY",
      value: "DAILY",
    },
    {
      label: "REALTIME",
      value: "REALTIME",
    },
  ];

  const isFormValidate = () => {
    let validate = true;

    if (!firstName) {
      setFirstNameErrorMessage("Please enter the first name");
      validate = false;
    }

    if (!lastName) {
      setLastNameErrorMessage("Please enter the last name");
      validate = false;
    }

    // const emailPattern = new RegExp(
    //   /^(("[\w-\s]+")|([\w-]+(?:\.[\w-]+)*)|("[\w-\s]+")([\w-]+(?:\.[\w-]+)*))(@((?:[\w-]+\.)*\w[\w-]{0,66})\.([a-z]{2,10}(?:\.[a-z]{2})?)$)|(@\[?((25[0-5]\.|2[0-4][0-9]\.|1[0-9]{2}\.|[0-9]{1,2}\.))((25[0-5]|2[0-4][0-9]|1[0-9]{2}|[0-9]{1,2})\.){2}(25[0-5]|2[0-4][0-9]|1[0-9]{2}|[0-9]{1,2})\]?$)/i,
    // );
    const hasEmailSubscription = entries.some(
      (entry) => entry.communicationType === "EMAIL",
    );
    if (hasEmailSubscription) {
      if (!email) {
        setEmailErrorMessage("Please enter the valid e-mail");
        validate = false;
      }
    } else {
      setEmailErrorMessage("");
    }

    const phonePattern = /^\+1[0-9]{10}$/;
    const hasSmsSubscription = entries.some(
      (entry) => entry.communicationType === "SMS",
    );
    if (hasSmsSubscription || phone?.length) {
      if (!phonePattern.test(phone)) {
        setPhoneErrorMessage("Please enter a valid phone number");
        validate = false;
      }
    } else {
      setPhoneErrorMessage("");
    }
    return validate;
  };

  const isStatusFormValidation = () => {
    let validate = true;

    if (!orderStatus) {
      setStatusErrorMessage("Please select");
      validate = false;
    }

    if (!communicationType) {
      setCommunicationTypeErrorMessage("Please select");
      validate = false;
    }

    if (!frequency) {
      setFrequencyErrorMessage("Please select");
      validate = false;
    } else if (
      communicationType?.value === "SMS" &&
      frequency?.value !== "REALTIME"
    ) {
      setFrequencyErrorMessage("For SMS, only 'Realtime' is allowed");
      validate = false;
    }

    if (orderStatus && communicationType && frequency) {
      const isDuplicate = entries.some(
        (entry) =>
          entry.orderStatus === orderStatus.label &&
          entry.communicationType === communicationType.label &&
          entry.frequency === frequency.label,
      );

      if (isDuplicate) {
        setDuplicateStatusErrorMessage(
          "This subscription has already been added.",
        );
        validate = false;
      }
    }

    return validate;
  };

  const onFirstNameChange = (e) => {
    clearErrors("cu-first-name");
    setFirstName(e.target.value);
  };

  const onLastNameChange = (e) => {
    clearErrors("cu-last-name");
    setLastName(e.target.value);
  };

  const onEmailChange = (e) => {
    clearErrors("cu-email");
    setEmail(e.target.value);
  };

  const onPhoneChange = (e) => {
    clearErrors("cu-phone");
    let val = e.target.value;
    val = val.replace(/\D/g, "");

    if (val.startsWith("1")) {
      val = val.slice(1);
    }

    val = val.slice(0, 10);
    const formatted = val?.length ? "+1" + val : val;

    setPhone(formatted);
  };

  const onStatusChange = (option) => {
    console.log("set status change", option);
    clearErrors("cu-order-status");
    setOrderStatus(option);
  };

  const onCommunicationTypeChange = (option) => {
    clearErrors("cu-communication-type");
    setCommunicationType(option);
  };

  const onFrequencyChange = (option) => {
    clearErrors("cu-frequency");
    setFrequency(option);
  };

  const onSmsConsentChange = (e) => {
    const isChecked = e.target.checked;
    setSmsConsent(isChecked);
  };

  const onDuplicateRecord = () => {
    clearErrors("cu-duplicate-value");
  };

  const clearErrors = (formField) => {
    setFirstNameErrorMessage(
      formField === "cu-first-name" ? "" : firstNameErrorMessage,
    );
    setLastNameErrorMessage(
      formField === "cu-last-name" ? "" : lastNameErrorMessage,
    );
    setEmailErrorMessage(formField === "cu-email" ? "" : emailErrorMessage);
    setPhoneErrorMessage(formField === "cu-phone" ? "" : phoneErrorMessage);
    setStatusErrorMessage(
      formField === "cu-order-status" ? "" : statusErrorMessage,
    );
    setCommunicationTypeErrorMessage(
      formField === "cu-communication-type"
        ? ""
        : communicationTypeErrorMessage,
    );
    setFrequencyErrorMessage(
      formField === "cu-frequency" ? "" : frequencyErrorMessage,
    );
    setDuplicateStatusErrorMessage(
      formField === "cu-duplicate-value" ? "" : duplicateStatusErrorMessage,
    );

    if (formField === "all") {
      setOnSubmiting(false);
      setFirstNameErrorMessage("");
      setLastNameErrorMessage("");
      setEmailErrorMessage("");
      setPhoneErrorMessage("");
      setStatusErrorMessage("");
      setCommunicationTypeErrorMessage("");
      setFrequencyErrorMessage("");
      setDuplicateStatusErrorMessage("");
    }
  };

  const clearField = () => {
    setFirstName("");
    setLastName("");
    setEmail("");
    setPhone("");
    setOrderStatus("");
    setCommunicationType("");
    setFrequency("");
    setSmsConsent(false);
  };

  const closePopup = () => {
    clearField();
    clearErrors("all");
    onClose();
  };

  const handleAdd = () => {
    setOnSubmiting(true);
    if (!isStatusFormValidation()) return;

    clearErrors("cu-duplicate-value");

    if (orderStatus && communicationType && frequency) {
      const isDuplicate = entries.some(
        (entry) =>
          entry.status === orderStatus.label &&
          entry.communication === communicationType.label &&
          entry.frequency === frequency.label,
      );

      if (isDuplicate) {
        setDuplicateStatusErrorMessage(
          "This subscription type is already exists.",
        );
        return;
      }

      setEntries([
        ...entries,
        {
          orderStatus: orderStatus.value,
          communicationType: communicationType.label,
          frequency: frequency.label,
        },
      ]);

      setOrderStatus(null);
      setCommunicationType(null);
      setFrequency(null);
    }
  };

  //Recipient save
  const handleSubmit = async (e) => {
    e.preventDefault();
    setOnSubmiting(true);
    if (!isFormValidate()) return;
    // if (!isFormValidate() && isStatusFormValidation()) return;

    if (entries.length === 0) {
      setDuplicateStatusErrorMessage("Please add at least one subscription.");
      return;
    }

    const newRecipient = {
      firstName,
      lastName,
      email,
      phoneNumber: phone,
      subscriptions: entries.map((entry) => ({
        orderStatus: entry.orderStatus,
        communicationType: entry.communicationType,
        frequency: entry.frequency,
      })),
    };

    // if (notification) {
    //   await updateRecipient({
    //     recipientId: notification.recipientId,
    //     payload: notification,
    //   });
    // } else {
    //   await createRecipient
    //     payload: notification,
    //   });
    // }

    onSave(newRecipient);

    // Clear form fields after save
    // setFirstName("");
    // setLastName("");
    // setEmail("");
    // setPhone("");
    // setEntries([]);
    // setOrderStatus(null);
    // setCommunicationType(null);
    // setFrequency(null);
    // setOnSubmiting(false);
  };

  const handleDelete = (indexToDelete) => {
    setEntries(entries.filter((_, index) => index !== indexToDelete));
  };

  return (
    <div
      className="info-overlay"
      style={{ display: visible ? "block" : "none", zIndex: 10 }}
    >
      <div className="tw-relative" id="create-notification" ref={modal}>
        {loading && (
          <div className="tw-absolute tw-z-10 tw-inset-0 tw-bg-white/50 tw-w-full tw-h-full tw-flex tw-items-center tw-justify-center">
            <Loader
              type="Oval"
              color="rgba(246,130,32,1)"
              height="50"
              width="50"
            />
          </div>
        )}

        <div id="create-notification-top">
          <div id="create-notification-header">
            <h2>
              {notification
                ? "Update Subscription"
                : "Create a New Subscription"}
            </h2>
            <div id="cu-close-button">
              <span className="glyphicon glyphicon-remove" onClick={onClose} />
            </div>
          </div>
        </div>

        <div className="create-notification-bottom">
          <form onSubmit={handleSubmit}>
            <div className="create-notification-bottom-container">
              <div className="cu-input-horizontal">
                <div className="form-group input-text cu-width-half cu-input-width-right">
                  <label htmlFor="first-name">FIRST NAME</label>
                  <Tooltip
                    content={firstNameErrorMessage}
                    show={onSubmiting && !!firstNameErrorMessage}
                    config={{
                      zIndex: 100,
                      theme: "error",
                      trigger: "manual",
                      hideOnClick: false,
                      placement: "top-end",
                    }}
                  >
                    <input
                      name="first-name"
                      id="cu-first-name"
                      className="css-bg1rzq-control"
                      type="text"
                      style={
                        onSubmiting === true && firstNameErrorMessage
                          ? { border: "1px solid red" }
                          : null
                      }
                      value={firstName}
                      onChange={(e) => onFirstNameChange(e)}
                      required
                    ></input>
                  </Tooltip>
                </div>

                <div className="form-group input-text cu-width-half cu-input-width-left mt-5">
                  <label htmlFor="last-name">LAST NAME</label>
                  <Tooltip
                    content={lastNameErrorMessage}
                    show={onSubmiting && !!lastNameErrorMessage}
                    config={{
                      zIndex: 100,
                      theme: "error",
                      trigger: "manual",
                      hideOnClick: false,
                      placement: "top-end",
                    }}
                  >
                    <input
                      name="last-name"
                      id="cu-last-name"
                      className="css-bg1rzq-control"
                      type="text"
                      style={
                        onSubmiting === true && lastNameErrorMessage
                          ? { border: "1px solid red" }
                          : null
                      }
                      value={lastName}
                      onChange={(e) => onLastNameChange(e)}
                    />
                  </Tooltip>
                </div>
              </div>

              <div className="cu-input-horizontal">
                <div className="form-group input-text cu-width-half cu-input-width-right">
                  <label htmlFor="email">EMAIL ADDRESS</label>
                  <Tooltip
                    content={emailErrorMessage}
                    show={onSubmiting && !!emailErrorMessage}
                    config={{
                      zIndex: 100,
                      theme: "error",
                      trigger: "manual",
                      hideOnClick: false,
                      placement: "top-end",
                    }}
                  >
                    <input
                      name="email"
                      id="cu-email"
                      className="css-bg1rzq-control"
                      type="text"
                      style={
                        onSubmiting && emailErrorMessage
                          ? { border: "1px solid red" }
                          : null
                      }
                      value={email}
                      onChange={(e) => onEmailChange(e)}
                    />
                  </Tooltip>
                </div>

                <div className="form-group input-text cu-width-half cu-input-width-left">
                  <label htmlFor="phone-number">PHONE NUMBER</label>
                  <Tooltip
                    content={phoneErrorMessage}
                    show={onSubmiting && !!phoneErrorMessage}
                    config={{
                      zIndex: 100,
                      theme: "error",
                      trigger: "manual",
                      hideOnClick: false,
                      placement: "top-end",
                    }}
                  >
                    <input
                      name="phone-number"
                      id="cu-phone"
                      className="css-bg1rzq-control"
                      type="text"
                      minLength={12}
                      maxLength={12}
                      pattern="^\+1[0-9]{10}$"
                      style={
                        onSubmiting === true && phoneErrorMessage
                          ? { border: "1px solid red" }
                          : null
                      }
                      value={phone}
                      onChange={onPhoneChange}
                    />
                  </Tooltip>
                </div>
              </div>

              {error && (
                <div
                  className="validation-message"
                  style={{ color: "red", fontSize: "13px" }}
                >
                  {error}
                </div>
              )}

              <hr></hr>
              {/* <span 
              className="validation-message"
              content={statusErrorMessage}
              show={onSubmiting && !!statusErrorMessage}
              id="cu-duplicate-value"
               config={{
                      zIndex: 100,
                      theme: "error",
                      trigger: "manual",
                      hideOnClick: false,
                }}
                >
              </span> */}

              <div className="cu-input-horizontal">
                <div className="form-group cu-input-width-right">
                  <label htmlFor="order-status">ORDER STATUS</label>
                  <Tooltip
                    content={statusErrorMessage}
                    show={onSubmiting && !!statusErrorMessage}
                    config={{
                      zIndex: 100,
                      theme: "error",
                      trigger: "manual",
                      hideOnClick: false,
                      placement: "top-end",
                    }}
                  >
                    <div>
                      <Select
                        id="cu-order-status"
                        name="order-status"
                        className="react-select"
                        options={orderStatuses?.filter(
                          (x) => x.isSubscriptionAvailable,
                        )}
                        styles={{
                          control: (provided, state) => ({
                            ...provided,
                            border:
                              onSubmiting && statusErrorMessage
                                ? "1px solid red"
                                : "1px solid rgba(126, 212,247,1)",
                          }),
                        }}
                        value={orderStatus}
                        onChange={(option) => onStatusChange(option)}
                        classNamePrefix="tosca"
                        getOptionLabel={(option) => option.value}
                        getOptionValue={(option) => option.value}
                      />
                    </div>
                  </Tooltip>
                </div>

                <div className="form-group cu-input-width-right">
                  <label htmlFor="communication-type">COMMUNICATION TYPE</label>
                  <Tooltip
                    content={communicationTypeErrorMessage}
                    show={onSubmiting && !!communicationTypeErrorMessage}
                    config={{
                      zIndex: 100,
                      theme: "error",
                      trigger: "manual",
                      hideOnClick: false,
                      placement: "top-end",
                    }}
                  >
                    <div>
                      <Select
                        id="cu-communication-type"
                        name="communication-type"
                        className="react-select"
                        options={communicationTypeOptions}
                        styles={{
                          control: (provided, state) => ({
                            ...provided,
                            border:
                              onSubmiting && communicationTypeErrorMessage
                                ? "1px solid red"
                                : "1px solid rgba(126, 212,247,1)",
                          }),
                        }}
                        value={communicationType}
                        onChange={(option) => onCommunicationTypeChange(option)}
                        classNamePrefix="tosca"
                      />
                    </div>
                  </Tooltip>
                </div>

                <div className="form-group cu-input-width-right">
                  <label htmlFor="frequency">FREQUENCY</label>
                  <Tooltip
                    content={frequencyErrorMessage}
                    show={onSubmiting && !!frequencyErrorMessage}
                    config={{
                      zIndex: 100,
                      theme: "error",
                      trigger: "manual",
                      hideOnClick: false,
                      placement: "top-end",
                    }}
                  >
                    <div>
                      <Select
                        id="cu-frequency"
                        name="frequency"
                        className="react-select"
                        options={
                          communicationType?.value === "SMS"
                            ? frequencyOptions.filter(
                                (opt) => opt.value !== "DAILY",
                              )
                            : frequencyOptions
                        }
                        styles={{
                          control: (provided, state) => ({
                            ...provided,
                            border:
                              onSubmiting && frequencyErrorMessage
                                ? "1px solid red"
                                : "1px solid rgba(126, 212,247,1)",
                          }),
                        }}
                        value={frequency}
                        onChange={(option) => onFrequencyChange(option)}
                        classNamePrefix="tosca"
                      />
                    </div>
                  </Tooltip>
                </div>

                <div className="cu-footer">
                  <button
                    type="button"
                    className="button"
                    style={{ ...button, backgroundColor: "#FF8B2B" }}
                    onClick={handleAdd}
                  >
                    Add
                  </button>
                </div>
              </div>

              {onSubmiting && !!duplicateStatusErrorMessage && (
                <span
                  className="validation-message"
                  style={{ color: "red", fontSize: "13px" }}
                >
                  {duplicateStatusErrorMessage}
                </span>
              )}

              <div
                className="scroll-container"
                style={{ maxHeight: "200px", overflowY: "auto" }}
              >
                <table className="tw-min-w-full user-table">
                  <thead className="tw-pb-6">
                    <tr className="">
                      <th
                        className="tw-px-3 tw-pt-3 tw-pb-6 tw-border-b tw-border-gray-200 tw-bg-gray-50 tw-text-left tw-text-xs tw-leading-4 tw-font-semibold tw-text-gray-900 tw-tracking-wider tw-cursor-pointer tw-underline col-sort-header"
                        style={{
                          position: "relative",
                        }}
                      >
                        <div className="tw-w-3/4 tw-inline-block">Status</div>
                      </th>

                      <th
                        className="tw-px-3 tw-pt-3 tw-pb-6 tw-border-b tw-border-gray-200 tw-bg-gray-50 tw-text-left tw-text-xs tw-leading-4 tw-font-semibold tw-text-gray-900 tw-tracking-wider tw-cursor-pointer tw-underline col-sort-header"
                        style={{
                          position: "relative",
                        }}
                      >
                        <div className="tw-w-3/4 tw-inline-block">
                          Communication Type
                        </div>
                      </th>

                      <th
                        className="tw-px-3 tw-pt-3 tw-pb-6 tw-border-b tw-border-gray-200 tw-bg-gray-50 tw-text-left tw-text-xs tw-leading-4 tw-font-semibold tw-text-gray-900 tw-tracking-wider tw-cursor-pointer tw-underline col-sort-header"
                        style={{
                          position: "relative",
                        }}
                      >
                        <div className="tw-w-3/4 tw-inline-block">
                          frequency
                        </div>
                      </th>

                      <th
                        className="tw-px-3 tw-pt-3 tw-pb-6 tw-border-b tw-border-gray-200 tw-bg-gray-50 tw-text-left tw-text-xs tw-leading-4 tw-font-semibold tw-text-gray-900 tw-tracking-wider tw-cursor-pointer tw-underline col-sort-header"
                        style={{
                          position: "relative",
                        }}
                      >
                        <div className="tw-w-3/4 tw-inline-block"> </div>
                      </th>
                    </tr>
                  </thead>

                  <tbody className="tw-bg-white">
                    {entries.length === 0 ? (
                      <tr>
                        <td colSpan="4" className="tw-text-center tw-py-4">
                          No entries to display
                        </td>
                      </tr>
                    ) : (
                      entries.map((entry, index) => (
                        <tr
                          key={index}
                          className="hover:tw-bg-tosca-alice-blue"
                        >
                          <td className="tw-px-3 tw-py-4 tw-border-b">
                            {entry.orderStatus}
                          </td>
                          <td className="tw-px-3 tw-py-4 tw-border-b">
                            {entry.communicationType}
                          </td>
                          <td className="tw-px-3 tw-py-4 tw-border-b">
                            {entry.frequency}
                          </td>

                          <td className="tw-px-3 tw-py-4 tw-border-b">
                            <DeleteRecipientOption
                              authorized={true}
                              onConfirm={() => handleDelete(index)}
                            />
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              <div className="cu-footer">
                {entries.some((entry) => entry.communicationType === "SMS") && (
                  <div style={{ display: "flex" }}>
                    <input
                      type="checkbox"
                      id="sms_consent"
                      checked={smsConsent}
                      onChange={onSmsConsentChange}
                      className="tw-w-8 tw-h-8 tw-mx-2.5"
                    />
                    <label htmlFor="sms_consent" className="tw-ml-2">
                      I understand that by subscribing to SMS notifications, I
                      will receive text messages for the status changes I have
                      selected.
                    </label>
                  </div>
                )}

                <button
                  type="button"
                  onClick={closePopup}
                  style={{ ...button, backgroundColor: "#AFAFAF" }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="button"
                  onClick={handleSubmit}
                  disabled={
                    entries.some((e) => e.communicationType === "SMS") &&
                    !smsConsent
                  }
                  style={{
                    ...button,
                    backgroundColor:
                      entries.some((e) => e.communicationType === "SMS") &&
                      !smsConsent
                        ? "#AFAFAF"
                        : "#FF8B2B",
                    cursor:
                      entries.some((e) => e.communicationType === "SMS") &&
                      !smsConsent
                        ? "not-allowed"
                        : "pointer",
                    opacity:
                      entries.some((e) => e.communicationType === "SMS") &&
                      !smsConsent
                        ? 0.6
                        : 1,
                  }}
                >
                  {notification === null ? "Create" : "Update"}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

const button = {
  color: "#fff",
  padding: "0.5rem 1rem",
  border: "none",
  borderRadius: "6px",
  fontWeight: "500",
  fontSize: "14px",
};
